import { useRef, useState, useEffect } from 'react';
import { ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import { SWISS_CANTONS } from '../config/leadTaxonomy';
import { SWISS_CANTON_PATHS, SWISS_CANTON_LABEL_POS, SWISS_MAP_VIEWBOX_WIDTH, SWISS_MAP_VIEWBOX_HEIGHT } from '../config/swissCantonPaths';

interface Props {
  selected: Set<string>;
  onToggle: (canton: string) => void;
  disabled?: boolean;
}

interface ViewBox { x: number; y: number; w: number; h: number; }

const FULL_VIEW: ViewBox = { x: 0, y: 0, w: SWISS_MAP_VIEWBOX_WIDTH, h: SWISS_MAP_VIEWBOX_HEIGHT };
const MIN_W = SWISS_MAP_VIEWBOX_WIDTH / 10;
const MAX_W = SWISS_MAP_VIEWBOX_WIDTH;
// Sotto questo spostamento (in pixel schermo) un click viene ancora trattato
// come selezione del cantone invece che come drag di pan — altrimenti un
// drag minimo accidentale toglierebbe la possibilità di cliccare con precisione.
const DRAG_CLICK_THRESHOLD_PX = 4;

function clampView(v: ViewBox): ViewBox {
  const w = Math.min(MAX_W, Math.max(MIN_W, v.w));
  const h = w * (SWISS_MAP_VIEWBOX_HEIGHT / SWISS_MAP_VIEWBOX_WIDTH);
  const x = Math.min(SWISS_MAP_VIEWBOX_WIDTH - w, Math.max(0, v.x));
  const y = Math.min(SWISS_MAP_VIEWBOX_HEIGHT - h, Math.max(0, v.y));
  return { x, y, w, h };
}

/**
 * Mappa zoomabile e pannabile dei confini reali dei 26 cantoni svizzeri
 * (v. swissCantonPaths.ts, swisstopo Open Government Data) — cliccabili per
 * una selezione multipla cumulabile. Sostituisce la precedente mappa "a
 * bolle" su centroidi approssimati (18/9/2026, richiesta esplicita: "i
 * contorni della Svizzera e dei cantoni devono essere definiti").
 *
 * Zoom con la rotellina (centrato sul cursore) o i pulsanti +/-, pan con
 * drag del mouse — un drag minimo (v. DRAG_CLICK_THRESHOLD_PX) non conta
 * come click sul cantone, altrimenti un pan involontario selezionerebbe un
 * cantone a caso.
 */
export function SwissCantonMap({ selected, onToggle, disabled }: Props) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [view, setView] = useState<ViewBox>(FULL_VIEW);
  const dragState = useRef<{ startScreenX: number; startScreenY: number; startView: ViewBox; moved: boolean; pointerId: number; canton: string | null } | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const screenToViewScale = () => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return 1;
    return view.w / rect.width;
  };

  const zoomBy = (factor: number, centerScreen?: { x: number; y: number }) => {
    const svg = svgRef.current;
    const rect = svg?.getBoundingClientRect();
    let cx = view.x + view.w / 2;
    let cy = view.y + view.h / 2;
    if (rect && centerScreen) {
      const scale = view.w / rect.width;
      cx = view.x + (centerScreen.x - rect.left) * scale;
      cy = view.y + (centerScreen.y - rect.top) * scale;
    }
    const newW = view.w * factor;
    const newH = newW * (SWISS_MAP_VIEWBOX_HEIGHT / SWISS_MAP_VIEWBOX_WIDTH);
    const newX = cx - (cx - view.x) * (newW / view.w);
    const newY = cy - (cy - view.y) * (newH / view.h);
    setView(clampView({ x: newX, y: newY, w: newW, h: newH }));
  };

  // Wheel nativo (non-passive): il gestore React onWheel non può chiamare
  // preventDefault perché React lo registra come passive, e senza
  // preventDefault la pagina sotto scrollerebbe insieme allo zoom.
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      zoomBy(e.deltaY > 0 ? 1.15 : 1 / 1.15, { x: e.clientX, y: e.clientY });
    };
    svg.addEventListener('wheel', handler, { passive: false });
    return () => svg.removeEventListener('wheel', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const handlePointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
    if (disabled) return;
    // Il cantone va letto QUI, prima di setPointerCapture — una volta che
    // l'SVG cattura il puntatore, il target di pointerup diventa sempre
    // l'elemento che ha catturato (l'svg stesso, senza data-canton), mai il
    // <path> sotto il cursore: un bug reale trovato dal vivo il 18/9/2026
    // (i click reali dell'utente non selezionavano mai un cantone diverso
    // da quello già selezionato al mount, mentre i test automatizzati con
    // eventi sintetici lo mascheravano).
    const target = e.target as Element;
    const canton = target.closest('[data-canton]')?.getAttribute('data-canton') ?? null;
    dragState.current = { startScreenX: e.clientX, startScreenY: e.clientY, startView: view, moved: false, pointerId: e.pointerId, canton };
    svgRef.current?.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const drag = dragState.current;
    if (!drag) return;
    const dxScreen = e.clientX - drag.startScreenX;
    const dyScreen = e.clientY - drag.startScreenY;
    if (Math.hypot(dxScreen, dyScreen) > DRAG_CLICK_THRESHOLD_PX) {
      drag.moved = true;
      setIsDragging(true);
    }
    if (drag.moved) {
      const scale = screenToViewScale();
      setView(clampView({ ...drag.startView, x: drag.startView.x - dxScreen * scale, y: drag.startView.y - dyScreen * scale }));
    }
  };

  const handlePointerUp = (e: React.PointerEvent<SVGSVGElement>) => {
    const drag = dragState.current;
    svgRef.current?.releasePointerCapture(e.pointerId);
    setIsDragging(false);
    if (!drag) return;
    if (!drag.moved && !disabled && drag.canton) {
      onToggle(drag.canton);
    }
    dragState.current = null;
  };

  return (
    <div className="h-full flex flex-col">
      {/* overflow-hidden è la parte che mancava (bug reale trovato dal vivo
          18/9/2026): senza, l'altezza dell'SVG si calcolava dal suo
          rapporto d'aspetto (viewBox) invece che dal contenitore — su un
          modale largo risultava più alto del riquadro assegnato e
          traboccava visivamente sopra la checklist comuni sottostante,
          intercettandone i click sui checkbox. */}
      <div className="relative flex-1 min-h-0 overflow-hidden rounded-lg bg-blue-50/40">
        <svg
          ref={svgRef}
          viewBox={`${view.x} ${view.y} ${view.w} ${view.h}`}
          className={`absolute inset-0 w-full h-full select-none touch-none ${isDragging ? 'cursor-grabbing' : disabled ? 'cursor-not-allowed' : 'cursor-grab'}`}
          role="group"
          aria-label="Mappa cantoni svizzeri — selezione multipla, zoomabile"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
        >
          {SWISS_CANTONS.map((c) => {
            const d = SWISS_CANTON_PATHS[c.id];
            if (!d) return null;
            const isSelected = selected.has(c.id);
            return (
              <path
                key={c.id}
                data-canton={c.id}
                d={d}
                className={
                  isSelected
                    ? 'fill-navy-900 stroke-white hover:fill-navy-800'
                    : 'fill-gray-200 stroke-white hover:fill-gray-300'
                }
                strokeWidth={1}
              >
                <title>{c.name}</title>
              </path>
            );
          })}
          {SWISS_CANTONS.map((c) => {
            const pos = SWISS_CANTON_LABEL_POS[c.id];
            if (!pos) return null;
            const isSelected = selected.has(c.id);
            return (
              <text
                key={c.id}
                x={pos[0]}
                y={pos[1]}
                textAnchor="middle"
                dominantBaseline="central"
                className={`pointer-events-none font-semibold ${isSelected ? 'fill-white' : 'fill-gray-600'}`}
                style={{ fontSize: 11 }}
              >
                {c.id}
              </text>
            );
          })}
        </svg>
        <div className="absolute top-2 right-2 flex flex-col gap-1 bg-white/90 rounded-lg shadow p-1">
          <button
            type="button"
            onClick={() => zoomBy(1 / 1.4)}
            className="p-1.5 rounded hover:bg-gray-100 text-gray-600"
            title="Zoom avanti"
          >
            <ZoomIn size={15} />
          </button>
          <button
            type="button"
            onClick={() => zoomBy(1.4)}
            className="p-1.5 rounded hover:bg-gray-100 text-gray-600"
            title="Zoom indietro"
          >
            <ZoomOut size={15} />
          </button>
          <button
            type="button"
            onClick={() => setView(FULL_VIEW)}
            className="p-1.5 rounded hover:bg-gray-100 text-gray-600"
            title="Reimposta la vista"
          >
            <Maximize2 size={15} />
          </button>
        </div>
      </div>
      <p className="text-[10px] text-gray-400 mt-1 shrink-0">
        Confini reali (swisstopo) — rotellina o pulsanti per zoomare, trascina per spostarti, clicca uno o più cantoni per combinarli nella ricerca.
      </p>
    </div>
  );
}
